import { lazy, Suspense } from 'react';
import { Routes, BrowserRouter, Route } from 'react-router-dom';
import { Spin } from 'antd';

// Components
import Header from 'components/commonComponents/Header';
import Footer from 'components/commonComponents/Footer';
import RequireAuth from 'components/commonComponents/RequireAuth';

// The landing page is the one route that must not wait on a chunk: it is both
// "/" and the catch-all, so it is what a cold visit paints first. Everything
// else is split out of the main bundle and fetched when its route is matched.
import HomePage from 'components/pages/HomePage';

// About
const About = lazy(() => import('components/pages/About'));
const Founder = lazy(() => import('components/pages/About/Founder'));
const Contact = lazy(() => import('components/pages/Contact'));
const HelpAndSupport = lazy(() => import('components/pages/HelpAndSupport'));

// Albums — the Rosarium songs archive, reached from the founder page
const Albums = lazy(() => import('components/pages/Albums'));
const ViewSong = lazy(() => import('components/pages/Albums/ViewSong'));
const Singers = lazy(() => import('components/pages/Albums/Singers'));
const ViewSinger = lazy(() => import('components/pages/Albums/ViewSinger'));

// Static / policy pages — one component, three page ids
const Policy = lazy(() => import('components/pages/StaticPages/Policy'));

// Account (behind RequireAuth)
const Settings = lazy(() => import('components/pages/Settings'));
const ReferAndEarn = lazy(() => import('components/pages/ReferAndEarn'));

// Subscription and payment
const Subscription = lazy(() => import('components/pages/Subscription'));
const SubscribePayment = lazy(() => import('components/pages/SubscribePayment'));
const Etisalat = lazy(() => import('components/pages/Etisalat'));
const Thanks = lazy(() => import('components/pages/Thanks'));

// Articles
const Articles = lazy(() => import('components/pages/Articles'));
const ViewArticle = lazy(() => import('components/pages/Articles/ViewArticle'));
const HEArticles = lazy(() => import('components/pages/HEArticles'));
const ApplicationArticles = lazy(() => import('components/pages/ApplicationArticles'));
const GeneralArticles = lazy(() => import('components/pages/GeneralArticles'));

// Awards — pulls in leaflet/react-leaflet for the map, so splitting it out
// keeps the mapping library off every other page.
const Awards = lazy(() => import('components/pages/Awards'));
const AwardsAll = lazy(() => import('components/pages/Awards/AwardsAll'));
const BookView = lazy(() => import('components/pages/Awards/BookView'));
const BookMap = lazy(() => import('components/pages/Awards/Maps'));

// Projects
const Projects = lazy(() => import('components/pages/Projects'));
const ViewApp = lazy(() => import('components/pages/Projects/ViewApp'));

// Everything else
const News = lazy(() => import('components/pages/News'));
const Competitions = lazy(() => import('components/pages/Competitions'));
const Gallery = lazy(() => import('components/pages/Gallery'));
const SearchPage = lazy(() => import('components/pages/Search'));
const HealthCheck = lazy(() => import('components/pages/HealthCheck'));

// Shown only while a route's chunk is in flight. It matches the spinner the
// pages themselves use for their own loading, so a slow network reads as the
// page loading rather than as a different kind of wait.
const RouteFallback = () => (
  <div className='container __innerTopPad text-center'>
    <Spin className='__spin_color' size='large' />
  </div>
);

const AppRouter = () => {
  return (
    <>
      <BrowserRouter>
        <div className='__dark'>
          <Header />

          <div className="content-wrapper">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Home */}
                <Route path="/home" element={<HomePage />} />

                {/* About */}
                <Route path="/about" element={<About />} />
                <Route path="/founder" element={<Founder />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/help-and-support" element={<HelpAndSupport />} />

                {/* Albums */}
                <Route path="/albums" element={<Albums />} />
                <Route path="/album/:songId" element={<ViewSong />} />
                <Route path="/singers" element={<Singers />} />
                <Route path="/singer/:singerId" element={<ViewSinger />} />

                {/* Policies */}
                <Route path="/privacy" element={<Policy id='59' />} />
                <Route path="/cookie" element={<Policy id='46' />} />
                <Route path="/terms" element={<Policy id='47' />} />

                {/* Account — the guard is declared once for the whole group */}
                <Route element={<RequireAuth />}>
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/refer-earn" element={<ReferAndEarn />} />
                </Route>

                {/* Subscription and payment */}
                <Route path="/subscribe/:appId" element={<Subscription />} />
                <Route path="/subscription-payment/:subId/:appId" element={<SubscribePayment />} />
                <Route path="/etisalat/:subId" element={<Etisalat />} />
                <Route path="/thanks" element={<Thanks />} />

                {/* Articles */}
                <Route path="/articles" element={<Articles />} />
                <Route path="/article/:articleId" element={<ViewArticle />} />
                <Route path="/he-articles" element={<HEArticles />} />
                <Route path="/application-articles" element={<ApplicationArticles />} />
                <Route path="/general-articles" element={<GeneralArticles />} />

                {/* Awards. "/book/map" is static and "/book/:bookId" dynamic,
                    so the router ranks the literal path higher regardless of
                    the order they are written in. */}
                <Route path="/awards" element={<Awards />} />
                <Route path="/awards/list/:type/:typeId" element={<AwardsAll />} />
                <Route path="/book/map" element={<BookMap />} />
                <Route path="/book/:bookId" element={<BookView />} />

                {/* Projects */}
                <Route path="/projects" element={<Projects />} />
                <Route path="/project/:appId" element={<ViewApp />} />

                {/* Everything else */}
                <Route path="/news" element={<News />} />
                <Route path="/competitions" element={<Competitions />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/healthcheck" element={<HealthCheck />} />

                {/* Catch-all, last by convention — v6 ranks it lowest anyway */}
                <Route path="*" element={<HomePage />} />
              </Routes>
            </Suspense>
          </div>

          <Footer />
        </div>
      </BrowserRouter>
    </>
  );
};

export default AppRouter;
